import React, { useRef, useEffect, useState } from "react";
import { Box, Button, Typography, IconButton, Paper } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import html2canvas from "html2canvas";

const EGCanvasTool = ({ onCapture, initialData }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;

    if (initialData) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = initialData;
    }
  }, [initialData]);

  // Listen for global capture event (triggered on Submit)
  useEffect(() => {
    const handleGlobalCapture = () => {
      captureCanvas();
    };
    window.addEventListener("trigger-all-captures", handleGlobalCapture);
    return () => window.removeEventListener("trigger-all-captures", handleGlobalCapture);
  }, []);

  const startDrawing = (e) => {
    const { offsetX, offsetY } = e.nativeEvent;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = e.nativeEvent;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    captureCanvas();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onCapture(null);
  };

  const captureCanvas = async () => {
    if (containerRef.current) {
      const canvas = await html2canvas(containerRef.current);
      const dataUrl = canvas.toDataURL("image/png");
      onCapture(dataUrl);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#1976d2" }}>
        Drawing Canvas:
      </Typography>
      <Paper
        ref={containerRef}
        elevation={2}
        sx={{
          border: "2px dashed #1976d2",
          borderRadius: "8px",
          position: "relative",
          width: "100%",
          maxWidth: "600px",
          height: "300px",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
      >
        <canvas
          ref={canvasRef}
          width={600}
          height={300}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseOut={stopDrawing}
          style={{ cursor: "crosshair", width: "100%", height: "100%" }}
        />
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton onClick={clearCanvas} size="small" sx={{ backgroundColor: "rgba(255,255,255,0.8)", "&:hover": { backgroundColor: "#fff" } }}>
            <ClearIcon fontSize="small" color="error" />
          </IconButton>
        </Box>
      </Paper>
      <Typography variant="caption" color="textSecondary">
        * Draw your circuit diagram or logic above. It will be saved automatically.
      </Typography>
    </Box>
  );
};

export default EGCanvasTool;
